/**
 * Team Members Hooks
 *
 * React Query hooks for fetching team members data.
 *
 * Archive Safety:
 * - Team members have an 'active' field (boolean)
 * - useTeamMembers returns ALL members (for admin views)
 * - useActiveTeamMembers returns only active members (for selectors)
 *
 * Role Safety:
 * - Normalizes roles to handle missing/invalid values
 * - Supports dual-field migration (teamRole + role)
 */

import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { collection, doc, getDoc, getDocs } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useRequiredTeamId } from './useRequiredTeamId';
import { ActiveTeamMember, isTeamMemberActive } from '@/types/entities';
import type { TeamMember } from '@/types/team';
import { resolveTeamMemberRole } from '@/lib/authz/normalizeRole';

/**
 * Hook to fetch all team members
 */
export function useTeamMembers() {
  const teamId = useRequiredTeamId();

  return useQuery({
    queryKey: ['teamMembers', teamId],
    queryFn: async () => {
      const snapshot = await getDocs(collection(db, 'teams', teamId, 'members'));
      return snapshot.docs.map((doc) => ({
        uid: doc.id,
        ...doc.data(),
      })) as TeamMember[];
    },
    enabled: !!teamId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to fetch only active team members (for selectors)
 *
 * Selector hook – guaranteed to return only active team members.
 * Use this for dropdowns and assignment selectors.
 *
 * @returns Only active team members
 */
export function useActiveTeamMembers(): UseQueryResult<ActiveTeamMember<TeamMember>[]> {
  const teamId = useRequiredTeamId();

  return useQuery({
    queryKey: ['activeTeamMembers', teamId],
    queryFn: async (): Promise<ActiveTeamMember<TeamMember>[]> => {
      const snapshot = await getDocs(collection(db, 'teams', teamId, 'members'));
      const allMembers = snapshot.docs.map((doc) => {
        const data = doc.data();
        // Normalize role to handle missing/invalid values and dual-field migration
        const normalizedRole = resolveTeamMemberRole({ ...data, uid: doc.id }, `member ${doc.id}`);

        return {
          uid: doc.id,
          ...data,
          role: normalizedRole, // Ensure role is always present and valid
        };
      }) as TeamMember[];

      // Filter to only active members and narrow type
      // Note: active field defaults to true if not set (for backward compatibility)
      const activeMembers = allMembers.filter(isTeamMemberActive);

      return activeMembers;
    },
    enabled: !!teamId,
    staleTime: 60 * 1000, // 1 minute
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}

/**
 * Hook to fetch a single team member by UID
 */
export function useTeamMember(uid: string | null | undefined) {
  const teamId = useRequiredTeamId();

  return useQuery({
    queryKey: ['teamMember', teamId, uid],
    queryFn: async () => {
      if (!uid) return null;

      const memberRef = doc(db, 'teams', teamId, 'members', uid);
      const memberSnap = await getDoc(memberRef);

      if (!memberSnap.exists()) {
        return null;
      }

      const memberData = memberSnap.data();

      // Try to fetch user profile data for name information
      // Use the correct path: /users/{uid}/profile/data
      try {
        const profileRef = doc(db, 'users', uid, 'profile', 'data');
        const profileSnap = await getDoc(profileRef);

        if (profileSnap.exists()) {
          const profileData = profileSnap.data();
          return {
            uid: memberSnap.id,
            ...memberData,
            email: profileData.email,
            displayName: profileData.displayName,
            firstName: profileData.firstName,
            lastName: profileData.lastName,
          } as TeamMember;
        }
      } catch {
        // Profile not found or permission denied - silently fall back to member data
      }

      // Fallback to just member data
      return {
        uid: memberSnap.id,
        ...memberData,
      } as TeamMember;
    },
    enabled: !!teamId && !!uid,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}
